import { useEffect, useId, useMemo, useRef, useState } from 'react'
import { Check, ChevronsUpDown, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Glass } from '@/components/ui/glass'
import { emptyFacet, type FacetMode, type FacetOption, type FacetValue } from '@/lib/facet'
import { cn } from '@/lib/utils'

function triggerLabel(facet: FacetValue, options: FacetOption[], placeholder: string): string {
  const labels = facet.values.map((item) => options.find((option) => option.value === item)?.label ?? item)
  if (labels.length === 0) return placeholder
  const prefix = facet.mode === 'nin' ? 'Exclude ' : ''
  if (labels.length <= 2) return `${prefix}${labels.join(', ')}`
  return `${prefix}${labels.length} selected`
}

function FacetPanel({
  label,
  facet,
  filtered,
  query,
  onQuery,
  onMode,
  onToggle,
}: {
  label: string
  facet: FacetValue
  filtered: FacetOption[]
  query: string
  onQuery: (value: string) => void
  onMode: (mode: FacetMode) => void
  onToggle: (value: string) => void
}) {
  const segment = (mode: FacetMode, text: string) => (
    <button
      type="button"
      aria-pressed={facet.mode === mode}
      onClick={() => onMode(mode)}
      className={cn(
        'h-8 flex-1 rounded-full text-sm font-semibold transition-colors focus-visible:outline-2 focus-visible:outline-solid focus-visible:outline-ring max-sm:h-11',
        facet.mode === mode ? 'bg-primary text-primary-foreground' : 'hover:bg-[var(--glass-hover)]',
      )}
    >
      {text}
    </button>
  )
  return (
    <Glass
      radius={18}
      role="group"
      aria-label={`${label} filter`}
      className="absolute top-[calc(100%+6px)] z-40 w-[min(22rem,calc(100vw-2rem))] p-2"
    >
      <div>
        <div className="mb-2 flex gap-1 rounded-full bg-[var(--glass-hover)] p-1">
          {segment('in', 'Include')}
          {segment('nin', 'Exclude')}
        </div>
        <input
          className="mb-2 h-9 w-full rounded-full border border-input bg-[rgb(var(--glass-tint)/0.6)] px-3 text-sm focus-visible:outline-2 focus-visible:outline-solid focus-visible:outline-ring max-sm:h-11"
          value={query}
          onChange={(e) => onQuery(e.target.value)}
          placeholder="Search"
          aria-label={`Search ${label} options`}
        />
        <div className="max-h-56 overflow-y-auto">
          {filtered.length === 0 ? <p className="px-2 py-4 text-center text-sm text-muted-foreground">No options match</p> : null}
          {filtered.map((option) => {
            const on = facet.values.includes(option.value)
            return (
              <button
                key={option.value}
                type="button"
                role="checkbox"
                aria-checked={on}
                className="flex min-h-8 w-full items-center gap-2 rounded-lg px-2 text-left text-sm hover:bg-[var(--glass-hover)] focus-visible:outline-2 focus-visible:outline-solid focus-visible:-outline-offset-2 focus-visible:outline-ring max-sm:min-h-11"
                onClick={() => onToggle(option.value)}
              >
                {/* A visible box on every option, so it reads as a multi-select before anything is ticked. */}
                <span
                  aria-hidden
                  className={cn(
                    'flex size-4 shrink-0 items-center justify-center rounded-sm border',
                    on ? 'border-primary bg-primary text-primary-foreground' : 'border-input bg-card',
                  )}
                >
                  {on ? <Check className="size-3" strokeWidth={3} /> : null}
                </span>
                {option.label}
              </button>
            )
          })}
        </div>
      </div>
    </Glass>
  )
}

export default function FacetFilter({
  label,
  value,
  options,
  onChange,
  placeholder = 'All',
}: {
  label: string
  value: FacetValue
  options: FacetOption[]
  onChange: (next: FacetValue) => void
  placeholder?: string
}) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const root = useRef<HTMLDivElement>(null)
  const trigger = useRef<HTMLButtonElement>(null)
  const labelId = useId()
  const facet = value.values.length ? value : { ...emptyFacet(), mode: value.mode }
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return options
    return options.filter((option) => option.label.toLowerCase().includes(q) || option.value.toLowerCase().includes(q))
  }, [options, query])

  useEffect(() => {
    if (!open) return
    function onDoc(event: MouseEvent) {
      if (!root.current?.contains(event.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onDoc)
    return () => document.removeEventListener('mousedown', onDoc)
  }, [open])

  const hasSelection = facet.values.length > 0
  return (
    <div
      ref={root}
      className="relative flex min-w-[160px] flex-col gap-1.5 max-lg:w-full"
      onKeyDown={(e) => {
        if (e.key === 'Escape' && open) {
          e.stopPropagation()
          setOpen(false)
          trigger.current?.focus()
        }
      }}
    >
      <span id={labelId} className="text-[13px] font-semibold text-muted-foreground">
        {label}
      </span>
      <div className="relative">
        <Button
          ref={trigger}
          type="button"
          variant="outline"
          aria-expanded={open}
          aria-labelledby={labelId}
          className={cn('h-9 w-full justify-between font-normal', hasSelection && 'pr-10', !hasSelection && 'text-muted-foreground')}
          onClick={() => setOpen((v) => !v)}
        >
          <span className="flex min-w-0 items-center gap-1 truncate">
            {facet.mode === 'nin' && hasSelection ? (
              <span className="shrink-0 rounded border border-destructive px-1 text-[11px] font-semibold text-destructive">Exclude</span>
            ) : null}
            <span className="truncate">{triggerLabel(facet, options, placeholder)}</span>
          </span>
          {hasSelection ? null : <ChevronsUpDown className="size-4 shrink-0 opacity-60" aria-hidden />}
        </Button>
        {hasSelection ? (
          <button
            type="button"
            aria-label={`Clear ${label} filter`}
            className="absolute top-1/2 right-1 flex size-7 -translate-y-1/2 items-center justify-center rounded text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:outline-2 focus-visible:outline-ring max-sm:size-11"
            onClick={() => {
              onChange(emptyFacet())
              setQuery('')
              trigger.current?.focus()
            }}
          >
            <X className="size-4" aria-hidden />
          </button>
        ) : null}
      </div>
      {open ? (
        <FacetPanel
          label={label}
          facet={facet}
          filtered={filtered}
          query={query}
          onQuery={setQuery}
          onMode={(mode) => onChange({ values: facet.values, mode })}
          onToggle={(item) => {
            const selected = facet.values.includes(item)
            const values = selected ? facet.values.filter((v) => v !== item) : [...facet.values, item]
            onChange({ values, mode: values.length ? facet.mode : 'in' })
          }}
        />
      ) : null}
    </div>
  )
}
